import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Filter, Share2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Capacitor } from '@capacitor/core';
import { Share as CapShare } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';
import WineCard from '@/components/WineCard';
import { useInventory } from '@/context/InventoryContext';
import { useAuth } from '@/context/AuthContext';
import { useScrollSentinel } from '@/hooks/useScrollSentinel';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useHomePrices } from '@/hooks/useHomePrices';
import { IconButton, Heading, MonoLabel, Body, PageHeader, Button, showToast } from '@/components/rc';
import { SortMenu } from '@/components/SortMenu';
import DowngradeBanner from '@/components/DowngradeBanner';
import { createShare } from '@/services/shareService';
import { trackEvent } from '@/config/analytics';
import { WEB_ORIGIN } from '@/utils/webOrigin';
import type { SortField } from '@/types';

const PAGE_SIZE = 40;

/* ── Condensed sticky header ─────────────────────────── */
const CondensedHeader: React.FC<{
  isPastHero: boolean;
  totalBottles: number;
  totalLabels: number;
  activeFilterCount: number;
  onFilterPress: () => void;
  sortField: SortField;
  setSortField: (f: SortField) => void;
  onShare: () => void;
}> = ({ isPastHero, totalBottles, totalLabels, activeFilterCount, onFilterPress, sortField, setSortField, onShare }) => (
  <div
    className="sticky top-0 left-0 right-0 z-[var(--rc-z-sticky)] flex items-center gap-3 sm:gap-4 px-4 sm:px-10 bg-[var(--rc-surface-elevated)] border-b border-[var(--rc-border-subtle)] transition-opacity duration-150 motion-reduce:transition-none"
    style={{
      height: 56,
      opacity: isPastHero ? 1 : 0,
      pointerEvents: isPastHero ? 'auto' : 'none',
    }}
  >
    {/* Counts — left */}
    <div className="flex gap-3 sm:gap-4 items-center font-mono text-xs uppercase tracking-widest text-[var(--rc-ink-ghost)]">
      <span className="flex items-baseline gap-1">
        <span className="text-[var(--rc-accent-pink)] font-display text-lg">{totalBottles}</span> bottles
      </span>
      <span className="flex items-baseline gap-1">
        <span className="text-[var(--rc-accent-acid)] font-display text-lg text-stroke-black">{totalLabels}</span> labels
      </span>
    </div>

    {/* Filter + Sort — right */}
    <div className="flex gap-2 items-center ml-auto flex-shrink-0">
      <div className="relative">
        <IconButton icon={Filter} aria-label="Filter" onClick={onFilterPress} />
        {activeFilterCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--rc-ink-primary)] text-[var(--rc-accent-acid)] font-[var(--rc-font-mono)] text-[9px] font-bold">
            {activeFilterCount}
          </span>
        )}
      </div>
      <SortMenu value={sortField} onChange={setSortField} iconButton />
      <IconButton icon={Share2} aria-label="Share cellar" onClick={onShare} />
    </div>
  </div>
);

const CellarPage: React.FC = () => {
  const {
    loading,
    filteredInventory,
    totalBottlesFiltered,
    heroWineIds,
    clearFilters,
    setSelectedWine,
    handleUpdate,
    sortField,
    setSortField,
    activeFilterCount,
    filters,
    setMobileFiltersOpen,
    openScan,
  } = useInventory();

  const homePrices = useHomePrices(filteredInventory);
  const { user } = useAuth();
  const navigate = useNavigate();
  const avatarInitial = (user?.displayName?.[0] || user?.email?.[0] || '?').toUpperCase();

  const { sentinelRef, isPastHero } = useScrollSentinel();

  // Progressive rendering — show PAGE_SIZE at a time
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const hasMore = visibleCount < filteredInventory.length;
  const { sentinelRef: loadMoreRef, triggered, reset } = useInfiniteScroll(hasMore);

  // Load next page when sentinel triggers
  useEffect(() => {
    if (triggered) {
      setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filteredInventory.length));
      reset();
    }
  }, [triggered, reset, filteredInventory.length]);

  // Reset visible count and scroll position when filters/sort change
  // (NOT on every inventory data update — that causes scroll-jump-to-top)
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    scrollRef.current?.scrollTo(0, 0);
  }, [filters, sortField]);

  const visibleWines = filteredInventory.slice(0, visibleCount);

  const openFilters = () => setMobileFiltersOpen(true);

  const handleShare = useCallback(async () => {
    const shareId = await createShare(
      filteredInventory,
      totalBottlesFiltered,
      user?.displayName || 'A Wine Lover',
    );
    if (!shareId) {
      showToast({ tone: 'error', message: 'Failed to create share link' });
      return;
    }
    const url = `${WEB_ORIGIN}/share/${shareId}`;
    if (Capacitor.isNativePlatform()) {
      try {
        await CapShare.share({ title: 'My Wine Cellar', url, dialogTitle: 'Share your cellar' });
      } catch (err) {
        // User cancelled the native share sheet — copy as a fallback only on real errors
        if ((err as Error).message && !/cancel/i.test((err as Error).message)) {
          await Clipboard.write({ string: url });
          showToast({ tone: 'success', message: 'Link copied to clipboard' });
        }
      }
    } else if (navigator.share) {
      try {
        await navigator.share({ title: 'My Wine Cellar', url });
      } catch (err) {
        if ((err as DOMException).name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
          showToast({ tone: 'success', message: 'Link copied to clipboard' });
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
      showToast({ tone: 'success', message: 'Link copied to clipboard' });
    }
    trackEvent('share_cellar', { wineCount: filteredInventory.length });
  }, [filteredInventory, totalBottlesFiltered, user]);

  return (
    <div ref={scrollRef} data-scroll-container className="relative h-full overflow-y-auto overflow-x-hidden overscroll-contain">
      <CondensedHeader
        isPastHero={isPastHero}
        totalBottles={totalBottlesFiltered}
        totalLabels={filteredInventory.length}
        activeFilterCount={activeFilterCount}
        onFilterPress={openFilters}
        sortField={sortField}
        setSortField={setSortField}
        onShare={handleShare}
      />

      {/* Hero section — -mt-14 cancels the sticky header's 56px, keeping layout identical to the old absolute-positioned header */}
      <div className="p-4 sm:p-10 space-y-4 sm:space-y-6 -mt-14">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
          <div className="flex-1 w-full relative">
            <PageHeader title="THE COLLECTION" subtitle="Your cellar inventory" />
            <button
              onClick={() => navigate({ to: '/settings' })}
              className="absolute top-0 right-0 w-9 h-9 rounded-full overflow-hidden shrink-0 ring-2 ring-[var(--rc-accent-pink)] active:scale-90 transition-transform md:hidden"
              aria-label="Account settings"
            >
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Account"
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center font-[family-name:var(--rc-font-display)] font-bold text-sm"
                    style={{ backgroundColor: 'var(--rc-accent-pink)', color: 'var(--rc-ink-on-accent)' }}
                  >
                    {avatarInitial}
                  </div>
                )}
            </button>
          </div>

          <div className="flex gap-4 sm:gap-6 items-end flex-shrink-0">
            <div className="flex flex-col items-end">
              <span className="text-[var(--rc-accent-pink)] text-4xl sm:text-6xl font-display leading-none tracking-tight">{totalBottlesFiltered}</span>
              <span className="text-[9px] sm:text-xs font-mono uppercase tracking-widest text-[var(--rc-ink-primary)]">Bottles</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[var(--rc-accent-acid)] text-4xl sm:text-6xl font-display leading-none tracking-tight text-stroke-black">{filteredInventory.length}</span>
              <span className="text-[9px] sm:text-xs font-mono uppercase tracking-widest text-[var(--rc-ink-primary)]">Labels</span>
            </div>
            <div className="flex gap-2 items-center self-center">
              <div className="relative">
                <IconButton icon={Filter} aria-label="Filter" onClick={openFilters} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--rc-ink-primary)] text-[var(--rc-accent-acid)] font-[var(--rc-font-mono)] text-[9px] font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <SortMenu value={sortField} onChange={setSortField} iconButton />
              <IconButton icon={Share2} aria-label="Share cellar" onClick={handleShare} />
            </div>
          </div>
        </div>

        {/* Sentinel — marks bottom of hero */}
        <div ref={sentinelRef} />

        <DowngradeBanner />

        {loading ? (
          <div className="flex flex-col items-center justify-center py-40 gap-4">
            <Loader2 className="animate-spin text-[var(--rc-accent-pink)]" size={48} />
            <p className="font-mono text-sm sm:text-xl animate-pulse">SYNCHRONIZING...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8 pb-24 sm:pb-20">
              {visibleWines.map(wine => (
                <WineCard
                  key={wine.id}
                  wine={wine}
                  isHero={heroWineIds.includes(wine.id)}
                  onClick={() => { (document.activeElement as HTMLElement)?.blur(); setSelectedWine(wine); }}
                  onUpdate={(key, value) => handleUpdate(wine, key, value)}
                  homePrice={homePrices.get(wine.id)}
                />
              ))}
              {filteredInventory.length === 0 && (
                <div className="col-span-full py-20 sm:py-40 text-center border-2 border-dashed border-[var(--rc-ink-ghost)] bg-[var(--rc-surface-elevated)]/50 px-4 flex flex-col items-center">
                  <p className="font-display text-4xl sm:text-6xl text-[var(--rc-ink-ghost)]">Your cellar awaits its first libation.</p>
                  <p className="mt-3 font-mono text-sm text-[var(--rc-ink-ghost)]">Add a bottle to begin.</p>
                  <div className="mt-6">
                    <Button variantType="Primary" label="ADD YOUR FIRST WINE" onClick={() => openScan()} />
                  </div>
                </div>
              )}
            </div>

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                <Body size="caption" colour="ghost" align="centre">
                  Showing {visibleCount} of {filteredInventory.length} wines
                </Body>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CellarPage;
